import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import Paginator from 'components/paginator';
import { flexCenter } from '@seedlang/style_mixins';
import Text from 'components/text';
import { AppUI, ExerciseUI } from '@seedlang/state';
import { isPresent, isBlank } from '@seedlang/utils';
import WorksheetFilter from 'components/worksheet/worksheet_filter';
import Spinner from 'components/spinner';
import { Theme } from '@seedlang/constants';
import cx from 'classnames';
import { LockIcon, YoutubeIcon } from '@seedlang/icons';
import Stack from 'components/layout/stack';
import Link from 'components/link';
import ReactTooltip from 'react-tooltip';
import WorksheetLabel from 'components/worksheet/worksheet_label';
import Button from 'components/button/button';
import { episodeCodeToDisplay } from 'components/episode_code';

const PODCAST_REQUEST_TIMEOUT = 40_000;

const Wrapper = styled.div`
  padding-top: ${props => props.paddingTop};
`;

const TableWrapper = styled.div`
  flex: 1;
`;

const Table = styled.div`
  border-radius: ${props => props.borderRadius};
  overflow: hidden;
`;

const Row = styled.div`
  ${flexCenter()}
  position: relative;
  background: ${props => props.header ? '#CCC' : '#FFF'};
  cursor: ${props => props.header ? 'default' : 'pointer'};
  border-radius: ${props => props.borderRadius};
  margin: ${props => props.margin};
  padding: ${props => props.padding || '10px'};
  &:hover {
    background: ${props => props.header ? '#CCC' : 'whitesmoke'};
    .grayscale {
      filter: none;
    }
  }
`;

const LinkRow = styled(Link)`
  ${flexCenter()}
  position: relative;
  background: #FFF;
  cursor: pointer;
  border-radius: ${props => props.borderRadius};
  margin: ${props => props.margin};
  padding: ${props => props.padding || '10px'};
  text-decoration: none;
  color: inherit;
  &:hover {
    background: whitesmoke;
    text-decoration: none;
    color: inherit;
    .grayscale {
      filter: none;
    }
  }
`;

const Episode = styled.div`
  flex: 1;
  min-width: 0;
`;

const Thumbnail = styled.div`
  height: 60px;
  margin-right: 10px;
  flex-shrink: 0;
  position: relative;
  img {
    border-radius: 10px;
    height: 100%;
    width: auto;
    cursor: pointer;
  }
  .grayscale {
    filter: gray;
    filter: grayscale(1);
    transition: filter 0.3s ease;
  }
`;

const LockIconWrapper = styled.div`
  z-index: ${Theme.z['foreground']};
  width: 20px;
  height: 20px;
  border-radius: 8px;
  background: #777777;
  position: absolute;
  top: 1px;
  left: 1px;
  pointer-events: none;
  .icon-wrapper {
    svg {
      fill: #FFF;
      width: 8px;
      position: absolute;
      left: 6px;
      top: 4px;
    }
  }
`;

const Description = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  font-size: 14px;
  line-height: 20px;
  color: #a8a8a8;
`;

const DateAndDuration = styled.div`
  color: ${Theme.darkGray};
  font-size: 13px;
  line-height: 20px;
`;

const CompletionButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-right: 5px;
  cursor: pointer;
  color: ${props => props.completed ? Theme.green : Theme.gray};
  font-size: inherit;
  vertical-align: initial;
  
  &:hover {
    opacity: 0.8;
  }

  i {
    color: inherit;
    margin-top: 1px;
  }
`;

@observer
class WorksheetTable extends React.Component {
  @observable searchQuery = null;
  @observable dateSort = '-worksheets.published_at';
  @observable loadFailed = false;
  worksheetRequestId = 0;

  static defaultProps = {
    showFilters: true,
  };

  @computed get paginatorIds() {
    const params = {groupId: this.props.params?.groupId};
    if (this.selectedTagIds) {
      params['tag_ids'] = this.selectedTagIds;
    }
    return params;
  }

  @computed get sortParameter() {
    const showFreeWorksheetsFirst = !ExerciseUI.user.isMember || (AppUI.vocabBlockedUntilMigrated || AppUI.exercisesBlockedUntilMigrated);
    return [
      showFreeWorksheetsFirst && '-access_without_membership',
      '-pinned',
      this.dateSort,
    ].filter(Boolean).join(',');
  }

  @computed get routeName() {
    return AppUI.routeStore.routeName;
  }

  constructor(props) {
    super(props);
    if (!this.props.store.hasIndexData) {
      this.getWorksheets();
    }
  }
  getWorksheets = () => {
    const requestId = ++this.worksheetRequestId;
    this.loadFailed = false;
    this.props.store.clearFilter();
    if (isPresent(this.props.store.limit)) {
      this.props.store.setLimit();
    }
    const params = {
      filters: {...this.props.defaultWorksheetFilter},
      sort: this.sortParameter,
      queryStrings: {
        show_all: true,
        podcast: this.routeName === 'podcast.index',
        videos: this.routeName === 'videos.index',
      },
    };

    if (this.searchQuery) {
      params['filters']['worksheets.name'] = `~${this.searchQuery}~`;
    }

    this.props.store.setPage(1);
    this.props.store.getIndex({
      ...params,
      timeout: PODCAST_REQUEST_TIMEOUT,
      failureCallback: () => this.handleLoadFailure(requestId),
      onRequestError: () => this.handleLoadFailure(requestId),
    }, response => this.handleLoadSuccess(requestId, response));
  }
  handleLoadSuccess = (requestId, response) => {
    if (requestId === this.worksheetRequestId) {
      this.props.store.afterGetIndex(response);
    }
  }
  handleLoadFailure = requestId => {
    if (requestId === this.worksheetRequestId) {
      this.loadFailed = true;
    }
  }
  onSearchQueryChange = (val) => {
    this.searchQuery = val;
    clearTimeout(this.interval);
    if (isBlank(val)) {
      this.getWorksheets();
    } else if (val.length > 1) {
      this.interval = setTimeout(this.getWorksheets, 500);
    }
  }
  toggleSort = (field) => {
    if (this.dateSort.endsWith(field)) {
      this.dateSort = this.dateSort.startsWith('-') ? field : `-${field}`;
    } else {
      this.dateSort = `-${field}`;
    }
    this.getWorksheets();
  }
  getItemRoute = (item) => {
    if (this.props.getItemRoute) {
      return this.props.getItemRoute(item);
    }
    return { pathname: '#' };
  }
  toggleCompleted = (e, item) => {
    e.preventDefault();
    e.stopPropagation();

    if (this.props.onToggleCompleted) {
      this.props.onToggleCompleted(item);
    }
  }
  showFreeLabel = (worksheet) => {
    return worksheet.published
      && worksheet.accessWithoutMembership
      && (!ExerciseUI.user.isMember || AppUI.isPaymentMigrationStartable);
  }
  cleanDescription = (worksheet) => {
    if (!worksheet.description) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = worksheet.description;
    return tempDiv.textContent || tempDiv.innerText || '';
  }
  worksheetTitle = (worksheet) => {
    if (worksheet.podcastEpisode && isPresent(worksheet.episodeCode)) {
      return `${episodeCodeToDisplay(worksheet.episodeCode)}: ${worksheet.name}`;
    }
    return worksheet.name;
  }

  formatDuration = seconds => {
    if (!seconds) return '';

    const minutes = Math.ceil(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
      return remainingMinutes > 0
        ? `${hours} hour${hours > 1 ? 's' : ''} ${remainingMinutes} min`
        : `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `${minutes} min`;
  };
  onClick = (item) => {
    ExerciseUI.set('worksheetId', item.id);
    ExerciseUI.worksheetStore.setShowData(item);
  }

  componentDidMount() {
    AppUI.scrollToPageTop('auto', 'page-top');
  }

  render() {
    return (
      <Wrapper paddingTop={AppUI.layout.isMobile ? '15px' : null}>
        <Stack gap='sm' alignItems='stretch'>
          {
            this.props.showFilters &&
              <WorksheetFilter
                showFilters
                onUpdateFilter={this.getWorksheets}
                onSearchQueryChange={this.onSearchQueryChange}
                searchQuery={this.searchQuery}
                routeName={this.routeName}
                defaultWorksheetFilter={this.props.defaultWorksheetFilter}
                onToggleSort={this.toggleSort}
                sortDirection={this.dateSort.startsWith('-') ? 'desc' : 'asc'}
              />
          }
          <TableWrapper>
            <Table borderRadius={AppUI.layout.isMobile ? '0' : '10px'}>
              {
                this.props.store.requestCounter > 0 &&
                  <Spinner />
              }

              {
                this.props.store.requestCounter === 0 && this.loadFailed &&
                  <Row borderRadius={AppUI.layout.isDesktop ? '0 0 10px 10px' : ''}>
                    <Stack gap='sm' alignItems='center'>
                      <Text>Sorry, we couldn&apos;t load the podcast episodes.</Text>
                      <Button
                        onClick={this.getWorksheets}
                      >
                        Try again
                      </Button>
                    </Stack>
                  </Row>
              }

              {
                this.props.store.requestCounter === 0 && !this.loadFailed && !this.props.store.hasIndexData &&
                  <Row borderRadius={AppUI.layout.isDesktop ? '0 0 10px 10px' : ''}>
                    <Text>No episodes found</Text>
                  </Row>
              }

              {
                this.props.store.indexData.map((item, index) => (
                  <LinkRow
                    key={item.id}
                    to={this.getItemRoute(item)}
                    onClick={() => this.onClick(item)}
                    padding={AppUI.layout.isMobile ? '10px 10px 5px 10px' : '10px'}
                    borderRadius={index === this.props.store.indexData.length - 1 && !AppUI.layout.isMobile ? '0 0 10px 10px' : ''}
                  >
                    {
                      !item?.accessStatus?.hasAccess && !this.showFreeLabel(item) &&
                        <LockIconWrapper>
                          <LockIcon />
                        </LockIconWrapper>
                    }
                    <div style={{display: 'flex', flex: 1, minWidth: 0}}>
                      {item.podcastEpisode?.podcast?.imageUrl &&
                        <Thumbnail>
                          <WorksheetLabel
                            type='free'
                            worksheet={item}
                          />
                          <img
                            alt=''
                            src={item.podcastEpisode.podcast.imageUrl}
                            className={cx({grayscale: !item?.accessStatus?.hasAccess && !this.showFreeLabel(item)})}
                          />
                        </Thumbnail>
                      }
                      <Episode>
                        <Text bold>
                          {
                            !AppUI.layout.isMobile ?
                              <>
                                <CompletionButton
                                  type='button'
                                  completed={item.overrideCompleted}
                                  onClick={e => this.toggleCompleted(e, item)}
                                  aria-label={item.overrideCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                                  data-tip
                                  data-for={`completion-tooltip-${item.id}`}
                                >
                                  <i className='fa fa-check-circle' />
                                </CompletionButton>
                                <ReactTooltip
                                  id={`completion-tooltip-${item.id}`}
                                  place='top'
                                  type='dark'
                                  effect='solid'
                                >
                                  {item.overrideCompleted ? 'Mark as Incomplete' : 'Mark as Completed'}
                                </ReactTooltip>
                              </>
                            :
                              <i
                                className='fa fa-check-circle'
                                style={{
                                  color: item.overrideCompleted ? Theme.green : Theme.gray,
                                  marginRight: '5px',
                                  verticalAlign: 'initial',
                                }}
                              />
                          }
                          {this.worksheetTitle(item)}
                          {
                            item.youtubeUrl &&
                              <YoutubeIcon
                                fill='#fe0134'
                                style={{width: '18px', height: '18px', marginLeft: '5px', verticalAlign: '-6px', display: 'inline-block', overflow: 'hidden'}}
                              />
                          }
                        </Text>
                        {
                          !AppUI.layout.isMobile && (item.podcastEpisode?.subtitle || this.cleanDescription(item)) &&
                            <Description>
                              {item.podcastEpisode?.subtitle || this.cleanDescription(item)}
                            </Description>
                        }
                        <DateAndDuration>
                          {item.publishedAt ? item.publishedAt.fullTextFormattedDateWithFullYear : null}
                          {item.podcastEpisode?.duration && ` • ${this.formatDuration(item.podcastEpisode.duration)}`}
                        </DateAndDuration>
                      </Episode>
                    </div>
                  </LinkRow>
                ))
              }

              <Paginator
                onClickScrollToTop
                withSpinner
                store={this.props.store}
                ids={this.paginatorIds}
                queryStrings={{
                  show_all: true,
                  podcast: this.routeName === 'podcast.index',
                  videos: this.routeName === 'videos.index',
                }}
                margin='20px 0'
              />
            </Table>
          </TableWrapper>
        </Stack>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(WorksheetTable);